// src/News.js - Page actualités avec filtres avancés et infinite scroll
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './styles/ManagementPages.css';

// Labels des catégories
const CATEGORY_OPTIONS = [
  { id: 0, label: 'Non catégorisé', icon: '📄' },
  { id: 1, label: 'Or', icon: '🥇' },
  { id: 2, label: 'Argent', icon: '🥈' },
  { id: 3, label: 'Platine', icon: '💎' },
  { id: 4, label: 'Palladium', icon: '⚡' },
  { id: 5, label: 'Marché', icon: '📊' },
  { id: 6, label: 'Devises', icon: '💱' },
  { id: 7, label: 'Économie', icon: '💹' },
  { id: 8, label: 'Agences', icon: '🏢' },
  { id: 9, label: 'Actualités', icon: '📰' },
  { id: 10, label: 'Guides', icon: '📚' },
  { id: 12, label: 'Investissement', icon: '💰' },
  { id: 14, label: 'Événements', icon: '🎯' },
];

const formatDate = (dateString) => {
  if (!dateString) return 'n/a';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const News = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [stats, setStats] = useState({ total_published: 0 });
  
  // Filtres avancés
  const [sortBy, setSortBy] = useState('dateposted');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month, custom
  
  // Infinite scroll
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const loaderRef = useRef(null);

  // Calculer les dates selon le filtre rapide
  const getDateRange = useCallback(() => {
    const today = new Date();
    const formatDate = (date) => date.toISOString().split('T')[0];

    switch (dateFilter) {
      case 'today':
        return { start: formatDate(today), end: formatDate(today) };
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        return { start: formatDate(weekAgo), end: formatDate(today) };
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setMonth(today.getMonth() - 1);
        return { start: formatDate(monthAgo), end: formatDate(today) };
      case 'custom':
        return { start: startDate, end: endDate };
      default:
        return { start: null, end: null };
    }
  }, [dateFilter, startDate, endDate]);

  const fetchNews = useCallback(async (pageNum = 1, append = false) => {
    if (isLoading) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      
      const params = { 
        limit: 100,
        page: pageNum,
        sortBy,
        sortOrder
      };
      
      // Ajouter les filtres
      if (selectedCategories.length > 0) {
        params.categoryId = selectedCategories[0];
      }
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      // Filtres de dates
      const dateRange = getDateRange();
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;

      const response = await axios.get('/api/news', { 
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const newData = response.data.data;
      const pagination = response.data.pagination;

      if (append) {
        setNews(prev => [...prev, ...newData]);
      } else {
        setNews(newData);
      }

      setHasMore(pagination.page < pagination.pages);
      
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Erreur lors de la récupération des actualités');
    } finally {
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  }, [selectedCategories, searchTerm, isLoading, sortBy, sortOrder, getDateRange]);

  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/news/categories', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setCategories(response.data.data);
    } catch (err) {
      console.error('Erreur categories:', err);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/news/stats', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setStats(response.data.data);
    } catch (err) {
      console.error('Erreur stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchStats();
  }, [fetchCategories, fetchStats]);

  useEffect(() => {
    setPage(1);
    setNews([]);
    setHasMore(true);
    fetchNews(1, false);
  }, [selectedCategories, searchTerm, sortBy, sortOrder, dateFilter, startDate, endDate]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading && !isInitialLoad) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchNews(nextPage, true);
        }
      },
      { threshold: 0.1 }
    );

    const currentLoader = loaderRef.current;
    if (currentLoader) {
      observer.observe(currentLoader);
    }

    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
    };
  }, [hasMore, isLoading, page, fetchNews, isInitialLoad]);

  const toggleCategoryFilter = (categoryId) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [categoryId],
    );
  };

  const getCategoryLabel = (categoryId) =>
    CATEGORY_OPTIONS.find((c) => c.id === Number(categoryId))?.label || `Catégorie ${categoryId}`;

  const getCategoryIcon = (categoryId) =>
    CATEGORY_OPTIONS.find((c) => c.id === Number(categoryId))?.icon || '📄';

  const resetFilters = () => {
    setSelectedCategories([]);
    setSearchTerm('');
    setDateFilter('all');
    setStartDate('');
    setEndDate('');
    setSortBy('dateposted');
    setSortOrder('DESC');
  };

  const hasActiveFilters = 
    selectedCategories.length > 0 || 
    searchTerm.trim() !== '' || 
    dateFilter !== 'all' ||
    sortBy !== 'dateposted' ||
    sortOrder !== 'DESC';

  return (
    <div className="management-page">
      <div className="management-page__inner">
        <header className="management-header glass-panel">
          <div className="management-header__content">
            <h1 className="management-title">
              <span className="management-title__icon">📰</span>
              Actualités Godot & Fils
            </h1>
            <p className="management-subtitle">
              Consultez l'ensemble des actualités publiées, filtrez par catégorie, date et accédez
              au contenu complet de chaque article. Défilez pour charger plus d'articles.
            </p>
            <div className="management-meta">
              <span className="management-badge">
                {stats.total_published} articles publiés
              </span>
              <span className="management-badge">Chargement automatique au scroll</span>
            </div>
          </div>
          <div className="management-header__actions">
            <button 
              type="button" 
              className="mgmt-btn mgmt-btn--outline" 
              onClick={() => {
                setPage(1);
                setNews([]);
                setHasMore(true);
                fetchNews(1, false);
              }}
            >
              🔄 Actualiser
            </button>
          </div>
        </header>

        <section className="management-stats">
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Articles publiés</span>
            <span className="management-stat-value">{stats.total_published}</span>
            <span className="management-stat-meta">Base de données</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Chargés</span>
            <span className="management-stat-value">{news.length}</span>
            <span className="management-stat-meta">
              {hasMore ? 'Scroll pour plus' : 'Tous chargés'}
            </span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Page actuelle</span>
            <span className="management-stat-value">{page}</span>
            <span className="management-stat-meta">100 articles / page</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Tri</span>
            <span className="management-stat-value">
              {sortBy === 'titre_fr' ? 'A-Z' : sortBy === 'id' ? 'ID' : '📅'}
            </span>
            <span className="management-stat-meta">
              {sortOrder === 'ASC' ? '⬆️ Croissant' : '⬇️ Décroissant'}
            </span>
          </div>
        </section>

        {error && (
          <div className="management-alert error">
            <span>⚠️</span>
            {error}
          </div>
        )}

        <section className="management-section glass-panel">
          <div className="management-section__head">
            <h2 className="management-section__title">Filtrer les actualités</h2>
            <p className="management-section__subtitle">
              Affinez votre recherche par titre, catégorie, date et ordre de tri.
            </p>
          </div>
          
          {/* Ligne 1: Recherche + Catégories */}
          <div className="management-grid management-grid--two">
            <div className="management-form__field">
              <label htmlFor="news-search" className="management-form__label">
                🔍 Recherche
              </label>
              <input
                id="news-search"
                className="mgmt-input"
                type="text"
                placeholder="Titre, résumé, label..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className="management-form__field">
              <label className="management-form__label">
                📂 Catégories (sélection unique)
              </label>
              <div className="management-checkbox-grid">
                {CATEGORY_OPTIONS.filter((cat) =>
                  categories.some((c) => c.id_categorie === cat.id)
                ).map((option) => {
                  const count = categories.find((c) => c.id_categorie === option.id)?.count || 0;
                  return (
                    <label key={option.id} className="management-checkbox">
                      <input
                        type="checkbox"
                        value={option.id}
                        checked={selectedCategories.includes(option.id)}
                        onChange={() => toggleCategoryFilter(option.id)}
                      />
                      {option.icon} {option.label} ({count})
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Ligne 2: Filtres de dates */}
          <div className="management-grid management-grid--two" style={{ marginTop: '20px' }}>
            <div className="management-form__field">
              <label htmlFor="date-filter" className="management-form__label">
                📅 Période
              </label>
              <select
                id="date-filter"
                className="mgmt-input"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <option value="all">Toutes les dates</option>
                <option value="today">Aujourd'hui</option>
                <option value="week">7 derniers jours</option>
                <option value="month">30 derniers jours</option>
                <option value="custom">Période personnalisée</option>
              </select>
            </div>
            
            {dateFilter === 'custom' && (
              <div className="management-form__field">
                <label className="management-form__label">
                  📆 Plage de dates
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="date"
                    className="mgmt-input"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    placeholder="Date début"
                  />
                  <input
                    type="date"
                    className="mgmt-input"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    placeholder="Date fin"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Ligne 3: Tri */}
          <div className="management-grid management-grid--two" style={{ marginTop: '20px' }}>
            <div className="management-form__field">
              <label htmlFor="sort-by" className="management-form__label">
                📊 Trier par
              </label>
              <select
                id="sort-by"
                className="mgmt-input"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="dateposted">Date de publication</option>
                <option value="titre_fr">Titre (A-Z)</option>
                <option value="id">ID</option>
              </select>
            </div>
            <div className="management-form__field">
              <label htmlFor="sort-order" className="management-form__label">
                ↕️ Ordre
              </label>
              <select
                id="sort-order"
                className="mgmt-input"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="DESC">⬇️ Décroissant (Z-A, Plus récent)</option>
                <option value="ASC">⬆️ Croissant (A-Z, Plus ancien)</option>
              </select>
            </div>
          </div>

          <div className="management-actions">
            <button
              type="button"
              className="mgmt-btn mgmt-btn--ghost"
              onClick={resetFilters}
            >
              🔄 Réinitialiser tous les filtres
            </button>
            {hasActiveFilters && (
              <span style={{ color: '#667eea', fontWeight: '600' }}>
                ✓ Filtres actifs
              </span>
            )}
          </div>
        </section>

        <section className="management-section glass-panel">
          <div className="management-section__head">
            <h2 className="management-section__title">Résultats</h2>
            <p className="management-section__subtitle">
              {news.length} actualité(s) chargée(s)
              {hasMore && ' — Défilez pour charger plus'}
            </p>
          </div>

          {isInitialLoad ? (
            <div className="management-empty-state">
              <span className="management-empty-state__icon">⏳</span>
              <span className="management-empty-state__title">Chargement des actualités...</span>
            </div>
          ) : news.length === 0 ? (
            <div className="management-empty-state">
              <span className="management-empty-state__icon">📭</span>
              <span className="management-empty-state__title">Aucune actualité trouvée</span>
              <span className="management-empty-state__subtitle">
                Ajustez vos filtres ou réinitialisez la recherche pour retrouver l'ensemble des
                articles.
              </span>
            </div>
          ) : (
            <>
              <div className="management-cards-grid">
                {news.map((article) => {
                  const imageUrl = article.image_une
                    ? `https://cdn.achat-or-et-argent.fr/ressources_v4/images/news/${article.image_une}`
                    : null;
                  const categoryLabel = getCategoryLabel(article.id_categorie);
                  const categoryIcon = getCategoryIcon(article.id_categorie);
                  
                  // URL SEO
                  const publicUrl = article.label_url_fr 
                    ? `https://www.achat-or-et-argent.fr/cours-de-l-or-et-argent-${article.id}-${article.label_url_fr}`
                    : `https://www.achat-or-et-argent.fr/actualites/${article.id}`;

                  return (
                    <article
                      key={article.id}
                      className="management-product-card glass-panel"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/news/${article.id}`)}
                    >
                      <div className="management-product-card__header">
                        <div className="management-product-card__title">
                          <h3>{article.label || article.titre_fr}</h3>
                          <span>ID: {article.id}</span>
                        </div>
                        <span className="management-tag management-tag--success">
                          {categoryIcon} {categoryLabel}
                        </span>
                      </div>

                      <div className="management-product-card__image">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={article.label || article.titre_fr}
                            loading="lazy"
                            onError={(e) => {
                              e.target.src =
                                'https://via.placeholder.com/400x250?text=Actualité';
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                              fontSize: '3rem',
                            }}
                          >
                            📰
                          </div>
                        )}
                      </div>

                      <div className="management-product-card__metrics">
                        <div className="management-metric" style={{ gridColumn: '1 / -1' }}>
                          <span className="management-metric__label">Résumé</span>
                          <span
                            className="management-metric__value"
                            style={{ fontSize: '0.85rem', lineHeight: '1.5' }}
                          >
                            {article.resume_fr?.substring(0, 120)}
                            {article.resume_fr?.length > 120 ? '...' : ''}
                          </span>
                        </div>
                        {article.label_url_fr && (
                          <div className="management-metric" style={{ gridColumn: '1 / -1' }}>
                            <span className="management-metric__label">🔗 Slug SEO</span>
                            <span
                              className="management-metric__value"
                              style={{ 
                                fontSize: '0.75rem', 
                                fontFamily: 'monospace',
                                color: '#667eea'
                              }}
                            >
                              {article.label_url_fr}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="management-product-card__footer">
                        <span>📅 {formatDate(article.dateposted)}</span>
                        <a
                          href={publicUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="management-tag"
                          style={{ 
                            background: '#e3f2fd', 
                            color: '#1976d2',
                            textDecoration: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          🌐 Site public
                        </a>
                      </div>
                    </article>
                  );
                })}
              </div>

              {/* Loader pour infinite scroll */}
              <div
                ref={loaderRef}
                style={{
                  height: '100px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '40px 0',
                }}
              >
                {isLoading && (
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        border: '4px solid #f3f3f3',
                        borderTop: '4px solid #667eea',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 10px',
                      }}
                    />
                    <p style={{ color: '#666', margin: 0 }}>
                      Chargement de 100 articles supplémentaires...
                    </p>
                  </div>
                )}
                {!isLoading && hasMore && (
                  <p style={{ color: '#999', textAlign: 'center' }}>
                    ⬇️ Défilez pour charger plus d'articles
                  </p>
                )}
                {!hasMore && news.length > 0 && (
                  <p style={{ color: '#667eea', fontWeight: '600', textAlign: 'center' }}>
                    ✅ Tous les articles ont été chargés ({news.length})
                  </p>
                )}
              </div>
            </>
          )}
        </section>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default News;
